import { useCallback, useEffect, useState, type MouseEvent as ReactMouseEvent } from "react";

interface UseExternalLinkHandlerOptions {
  allowedDomains?: string[];
  showModal?: boolean;
}

export const useExternalLinkHandler = (options: UseExternalLinkHandlerOptions = {}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string>("");
  const [pendingTarget, setPendingTarget] = useState<string>("");
  const [shouldNavigate, setShouldNavigate] = useState<{ url: string; target: string } | null>(null);

  const { allowedDomains = [], showModal = true } = options;

  const isExternalLink = useCallback((url: string): boolean => {
    try {
      // Проверяем, есть ли протокол в URL
      const urlToCheck = (!url.startsWith('http://') && !url.startsWith('https://')) 
        ? `https://${url}` 
        : url;
      
      const urlObj = new URL(urlToCheck);
      const currentDomain = window.location.hostname;
      
      // Если домен отличается от текущего
      if (urlObj.hostname !== currentDomain) {
        // Проверяем, не является ли домен разрешенным
        return !allowedDomains.some(domain => 
          urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`),
        );
      }
      
      return false;
    } catch {
      // Если URL некорректный, считаем его внешним
      return true;
    }
  }, [allowedDomains]);

  const handleLinkClick = useCallback((url: string, event?: ReactMouseEvent, target?: string) => {
    if (event) {
      event.preventDefault();
    }

    if (!isExternalLink(url)) {
      // Внутренняя ссылка - переходим сразу
      setShouldNavigate({ url, target: target || "" });
      return;
    }

    if (showModal) {
      // Внешняя ссылка - показываем модалку
      setPendingUrl(url);
      setPendingTarget(target || "");
      setIsModalOpen(true);
    } else {
      // Модалка отключена - переходим сразу
      setShouldNavigate({ url, target: target || "" });
    }
  }, [isExternalLink, showModal]);

  const confirmNavigation = useCallback(() => {
    if (pendingUrl) {
      setShouldNavigate({ url: pendingUrl, target: pendingTarget });
    }
    setIsModalOpen(false);
    setPendingUrl("");
    setPendingTarget("");
  }, [pendingUrl, pendingTarget]);

  const cancelNavigation = useCallback(() => {
    setIsModalOpen(false);
    setPendingUrl("");
    setPendingTarget("");
  }, []);

  // useEffect для выполнения навигации
  useEffect(() => {
    if (shouldNavigate) {
      if (shouldNavigate.target === "_blank") {
        window.open(shouldNavigate.url, "_blank");
      } else {
        window.location.href = shouldNavigate.url;
      }
      setShouldNavigate(null);
    }
  }, [shouldNavigate]);

  return {
    isModalOpen,
    pendingUrl,
    handleLinkClick,
    confirmNavigation,
    cancelNavigation,
    isExternalLink,
  };
}; 
